import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useStore } from 'zustand/react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  type DragCancelEvent,
  type DragEndEvent,
  type DragMoveEvent,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
} from '@dnd-kit/core';
import type { NameI18n, Zone } from '@displaycms/contracts';

import { Button } from '@/components/ui/button';
import { Alert } from '@/components/ui/alert';
import { pickName } from '@/lib/i18n-name';
import { Icon } from '@/components/icon';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { ApiError } from '@/api/client';
import { snapToGrid, type SnapStep } from '@/hooks/use-snap-to-grid';
import { useMultiSelect } from '@/hooks/use-multi-select';
import { useKeyboardNudge } from '@/hooks/use-keyboard-nudge';
import { useComposerHistory } from '@/hooks/use-composer-history';
import { useAuth } from '@/auth/auth-context';
import {
  FULL_ZONE_ID,
  getComposer,
  getComposerLayout,
  saveComposerLayout,
  type ComposerDetail,
  type LayoutPayload,
  type LayoutResponse,
} from '@/api/composers';
import { ZoneDraggable, type ResizeHandle } from '@/components/composer/zone-draggable';
import { PropertiesPanel } from '@/components/composer/properties-panel';
import { computeGuides, type Guide } from '@/hooks/use-alignment-guides';
import { AlignmentGuides } from '@/components/composer/alignment-guides';
import { CanvasMarquee } from '@/components/composer/canvas-marquee';
import { LayersPanel } from '@/components/composer/layers-panel';
import { PreviewPane, type ZoneBinding } from '@/components/composer/preview-pane';
import { rectsIntersect } from '@/lib/rects';
import { useRegisterSave } from './composer-save-registry';

/**
 * `/composers/:id/layout` — multi-zone WYSIWYG layout editor SCAFFOLD
 * (Phase 2, Wave 1C).
 *
 * This is the foundation: the route + page structure + drag/resize wiring
 * for `@dnd-kit/core`. Future waves add server persistence, properties depth,
 * the preview pane, template library, undo/redo, etc.
 *
 * Editor state shape:
 *  - `layout = { width_px, height_px, zones: Zone[] }` — the canonical shape
 *    from `@displaycms/contracts.composer.Layout` (id stripped — the row id is
 *    server-assigned on save).
 *  - `selectedIds` — multi-select via useMultiSelect (W2 T4); marquee-select
 *    (drag rectangle on canvas) is deferred to a future polish wave.
 *  - `lockedIds` — zones that are non-draggable; toggled via LayersPanel.
 *  - `gridStep` — snap-to-grid step in px; 1 means off (W2 T2).
 *  - `zoom` — visual scaling of the 1920×1080 reference canvas; persisted
 *    only in-memory.
 *
 * Server endpoints: `PUT /v1/admin/composers/:id/layout` saves geometry,
 * `GET /v1/admin/composers/:id/layout` returns the current version's saved
 * layout. If the save returns 404 the editor shows a "not yet wired" message
 * instead of a generic save error.
 *
 * Initial load: `layoutQuery` fetches the saved layout and the seed effect
 * reconstructs the canvas from those zones. A composer that never diverged
 * returns the full-screen singleton zone; a failed fetch falls back to the
 * same singleton, so the editor always opens onto a valid canvas.
 */

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const REFERENCE_WIDTH_PX = 1920;
const REFERENCE_HEIGHT_PX = 1080;

const ZOOM_PRESETS: ReadonlyArray<number> = [0.5, 0.75, 1.0, 1.25, 1.5];
const DEFAULT_ZOOM = 0.5;

// New zone defaults; a 200×200 square offset from origin so it doesn't
// overlap precisely on top of existing zones.
const NEW_ZONE_W = 200;
const NEW_ZONE_H = 200;
const NEW_ZONE_OFFSET_STEP = 40;

const ZONE_KINDS: ReadonlyArray<Zone['kind']> = [
  'media',
  'hybrid',
  'device',
  'text',
];

// ---------------------------------------------------------------------------
// Editor state model
// ---------------------------------------------------------------------------

function singletonZone(): Zone {
  return {
    id: FULL_ZONE_ID,
    x: 0,
    y: 0,
    w: REFERENCE_WIDTH_PX,
    h: REFERENCE_HEIGHT_PX,
    kind: 'media',
  };
}

function defaultZones(): Zone[] {
  return [singletonZone()];
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

// Pure resize geometry: apply a pointer delta to a zone for a given handle,
// snap + clamp, and return the new {x,y,w,h}. Width and height move
// independently (no aspect lock). Shared by the live-preview path
// (onDragMove) and the commit path (handleDragEnd) so they never diverge.
function computeResizedZone(
  zone: { x: number; y: number; w: number; h: number },
  handle: ResizeHandle,
  dx: number,
  dy: number,
  gridStep: SnapStep,
): { x: number; y: number; w: number; h: number } {
  let { x, y, w, h } = zone;
  if (handle === 'right' || handle === 'top-right' || handle === 'bottom-right') {
    w = w + dx;
  }
  if (handle === 'left' || handle === 'top-left' || handle === 'bottom-left') {
    x = x + dx;
    w = w - dx;
  }
  if (handle === 'bottom' || handle === 'bottom-left' || handle === 'bottom-right') {
    h = h + dy;
  }
  if (handle === 'top' || handle === 'top-left' || handle === 'top-right') {
    y = y + dy;
    h = h - dy;
  }
  const snappedSize = snapToGrid({ x: Math.round(w), y: Math.round(h) }, gridStep);
  const snappedPos = snapToGrid({ x: Math.round(x), y: Math.round(y) }, gridStep);
  w = clamp(snappedSize.x, 1, REFERENCE_WIDTH_PX);
  h = clamp(snappedSize.y, 1, REFERENCE_HEIGHT_PX);
  x = clamp(snappedPos.x, 0, REFERENCE_WIDTH_PX - w);
  y = clamp(snappedPos.y, 0, REFERENCE_HEIGHT_PX - h);
  return { x, y, w, h };
}

let zoneIdCounter = 0;
function nextZoneId(existing: ReadonlyArray<Zone>): string {
  // Stable across re-renders; collision-checked against existing zone ids
  // so manual id edits in a future wave don't accidentally duplicate.
  while (true) {
    zoneIdCounter += 1;
    const candidate = `zone-${zoneIdCounter}`;
    if (!existing.some((z) => z.id === candidate)) return candidate;
  }
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export function ComposerLayoutEditorPage({
  embedded = false,
}: { embedded?: boolean } = {}): JSX.Element {
  const { t, i18n } = useTranslation(['composers', 'common']);
  const params = useParams<{ id: string }>();
  const composerId = params.id ?? '';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();

  const lang: 'fr' | 'en' = i18n.language === 'en' ? 'en' : 'fr';

  const detailQuery = useQuery<ComposerDetail>({
    queryKey: ['composers', 'detail', composerId],
    queryFn: () => getComposer(composerId),
    enabled: composerId.length > 0,
    staleTime: 30_000,
  });

  // SP3d live preview: resolve each zone's FIRST item into a PreviewPane
  // binding so the "Aperçu" pane shows real content (image/video signed-URL
  // thumbnails, web/rss badges) instead of gradient placeholders. The item
  // kind `rss` maps to the `rss_feed` ResourceKind; composer/empty items have
  // no previewable content and are skipped. First item per zone represents it.
  const previewBindings: ZoneBinding[] = useMemo(() => {
    const byZone = new Map<string, ZoneBinding>();
    for (const prog of detailQuery.data?.programs ?? []) {
      for (const item of prog.items ?? []) {
        const zid = 'zone_id' in item ? item.zone_id : undefined;
        if (!zid || byZone.has(zid)) continue;
        if (item.kind === 'image' || item.kind === 'video') {
          byZone.set(zid, { zone_id: zid, kind: item.kind, resource_version_id: item.resource_version_id });
        } else if (item.kind === 'web_page') {
          byZone.set(zid, { zone_id: zid, kind: 'web_page', source_url: item.source_url });
        } else if (item.kind === 'rss') {
          byZone.set(zid, { zone_id: zid, kind: 'rss_feed', source_url: item.source_url });
        }
      }
    }
    return [...byZone.values()];
  }, [detailQuery.data]);

  // The saved geometry for this composer's current version. Seeds the
  // canvas on load so previously-saved multi-zone layouts round-trip.
  // On error we fall back to the singleton (see the seed effect below).
  const layoutQuery = useQuery<LayoutResponse>({
    queryKey: ['composers', 'layout', composerId],
    queryFn: () => getComposerLayout(composerId),
    enabled: composerId.length > 0,
    staleTime: 30_000,
  });

  const zones = useComposerHistory((s) => s.zones);
  const setZones = useComposerHistory((s) => s.setZones);
  const pastStates = useStore(useComposerHistory.temporal, (s) => s.pastStates);
  const futureStates = useStore(useComposerHistory.temporal, (s) => s.futureStates);
  const { selectedIds, select: selectZone, selectMany, clear: clearSelection } = useMultiSelect();
  const [lockedIds, setLockedIds] = useState<ReadonlyArray<string>>([]);
  // Grid defaults to off (1px = no snap) so width/height resize freely.
  // Operators who want snapping re-enable it via the "Grille" dropdown.
  const [gridStep, setGridStep] = useState<SnapStep>(1);
  const [zoom, setZoom] = useState<number>(DEFAULT_ZOOM);
  const [dirty, setDirty] = useState<boolean>(false);
  const [previewMode, setPreviewMode] = useState<boolean>(false);
  const [guides, setGuides] = useState<Guide[]>([]);
  // Transient geometry while a resize handle is being dragged, so the zone
  // visibly grows/shrinks during the drag (committed on drag end). Null when
  // no resize is in progress.
  const [liveResize, setLiveResize] = useState<
    { zoneId: string; x: number; y: number; w: number; h: number } | null
  >(null);
  const [saveMessageKey, setSaveMessageKey] = useState<
    | 'composers:layout_editor.save_success'
    | 'composers:layout_editor.save_error'
    | null
  >(null);

  const toggleLock = useCallback((id: string) => {
    setLockedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }, []);

  // Keyboard nudge: arrow keys move selected zones; Shift = 10px; Alt = gridStep.
  // Gated by canEdit (T5 carry-over fix). Locked zones filtered out of targets.
  // NOTE: canEdit is derived after the early-return guards below; we read it
  // from hasPermission directly here so the hook is called unconditionally
  // (Rules of Hooks require hooks to be called at the top level before any
  // conditional return).
  const canEditForNudge = hasPermission('composers.update');
  const onNudge = useCallback(
    (zoneIds: ReadonlyArray<string>, dx: number, dy: number) => {
      const currentZones = useComposerHistory.getState().zones;
      const nextZones = currentZones.map((z) => {
        if (!zoneIds.includes(z.id)) return z;
        const w = clamp(Math.max(1, Math.round(z.w)), 1, REFERENCE_WIDTH_PX);
        const h = clamp(Math.max(1, Math.round(z.h)), 1, REFERENCE_HEIGHT_PX);
        const x = clamp(Math.round(z.x + dx), 0, REFERENCE_WIDTH_PX - w);
        const y = clamp(Math.round(z.y + dy), 0, REFERENCE_HEIGHT_PX - h);
        return { ...z, x, y, w, h };
      });
      setZones(nextZones);
      setDirty(true);
    },
    [setZones],
  );

  const nudgeTargetIds = selectedIds.filter((id) => !lockedIds.includes(id));
  useKeyboardNudge({
    enabled: canEditForNudge,
    selectedIds: nudgeTargetIds,
    gridStep,
    onNudge,
  });

  // Cmd+Z / Cmd+Shift+Z — undo/redo via the zundo temporal store.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        useComposerHistory.temporal.getState().undo();
      } else if (e.key === 'z' && e.shiftKey) {
        e.preventDefault();
        useComposerHistory.temporal.getState().redo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Seed the editor once per composer load, from the saved layout. We
  // wait until BOTH the composer detail AND the layout query have
  // settled so we seed exactly once with the final state (seeding the
  // singleton first and swapping later would be blocked by initRef). If
  // the layout fetch failed, fall back to the full-screen singleton.
  // After seeding, clear the undo history so the loaded state is the
  // floor — Cmd+Z immediately after load should be a no-op.
  const initRef = useRef<string | null>(null);
  // True when the current seed came from an error fallback (singleton). Lets a
  // later success reseed exactly once, instead of locking to the fallback —
  // otherwise a transient layout-fetch error would permanently hide a saved
  // multi-zone layout until full page reload.
  const seededFromErrorRef = useRef(false);
  useEffect(() => {
    const data = detailQuery.data;
    if (!data) return;
    // Hold off until the layout query is no longer in flight.
    if (!layoutQuery.isSuccess && !layoutQuery.isError) return;
    const seededThis = initRef.current === data.id;
    // Reseed only when a prior error-fallback can now be replaced by a
    // successful fetch — and only if the user hasn't started editing the
    // fallback (never clobber unsaved edits). They can reload to pull the
    // saved layout if they'd rather discard their edits.
    const reseedAfterError =
      seededThis &&
      seededFromErrorRef.current &&
      layoutQuery.isSuccess &&
      !dirty;
    if (seededThis && !reseedAfterError) return;
    initRef.current = data.id;
    seededFromErrorRef.current = layoutQuery.isError;

    const fetched = layoutQuery.data?.zones;
    const seeded =
      fetched && fetched.length > 0 ? (fetched as Zone[]) : defaultZones();
    setZones(seeded);
    useComposerHistory.temporal.getState().clear();
    selectZone(seeded[0]?.id ?? FULL_ZONE_ID, false);
    setDirty(false);
  }, [
    detailQuery.data,
    layoutQuery.data,
    layoutQuery.isSuccess,
    layoutQuery.isError,
    dirty,
    selectZone,
    setZones,
  ]);

  const updateZone = useCallback(
    (zoneId: string, patch: Partial<Omit<Zone, 'id'>>) => {
      const currentZones = useComposerHistory.getState().zones;
      const nextZones = currentZones.map((z) => {
        if (z.id !== zoneId) return z;
        const merged: Zone = {
          ...z,
          ...patch,
        };
        // Clamp to reference bounds; keep width/height >= 1 px.
        const w = clamp(
          Math.max(1, Math.round(merged.w)),
          1,
          REFERENCE_WIDTH_PX,
        );
        const h = clamp(
          Math.max(1, Math.round(merged.h)),
          1,
          REFERENCE_HEIGHT_PX,
        );
        const x = clamp(Math.round(merged.x), 0, REFERENCE_WIDTH_PX - w);
        const y = clamp(Math.round(merged.y), 0, REFERENCE_HEIGHT_PX - h);
        return { ...merged, x, y, w, h };
      });
      setZones(nextZones);
      setDirty(true);
    },
    [setZones],
  );

  const addZone = useCallback(() => {
    const currentZones = useComposerHistory.getState().zones;
    const id = nextZoneId(currentZones);
    const offsetIdx = currentZones.length;
    const snapped = snapToGrid(
      { x: NEW_ZONE_OFFSET_STEP * offsetIdx, y: NEW_ZONE_OFFSET_STEP * offsetIdx },
      gridStep,
    );
    const newZone: Zone = {
      id,
      x: clamp(snapped.x, 0, REFERENCE_WIDTH_PX - NEW_ZONE_W),
      y: clamp(snapped.y, 0, REFERENCE_HEIGHT_PX - NEW_ZONE_H),
      w: NEW_ZONE_W,
      h: NEW_ZONE_H,
      kind: 'media',
    };
    setZones([...currentZones, newZone]);
    // Newly added zone is auto-selected so the properties panel reveals it.
    selectZone(id, false);
    setDirty(true);
  }, [gridStep, selectZone, setZones]);

  const removeZone = useCallback(
    (zoneId: string) => {
      const currentZones = useComposerHistory.getState().zones;
      setZones(currentZones.filter((z) => z.id !== zoneId));
      // Clear selection when the selected zone is removed.
      clearSelection();
      setDirty(true);
    },
    [clearSelection, setZones],
  );

  // Clone zones with fresh ids and a small offset so the copy sits visibly
  // on top of the source; geometry/kind/constraints/hints are copied
  // verbatim. The clones become the new selection.
  const cloneZonesWithOffset = useCallback(
    (sources: ReadonlyArray<Zone>): void => {
      if (sources.length === 0) return;
      const acc = [...useComposerHistory.getState().zones];
      const cloneIds: string[] = [];
      for (const src of sources) {
        const id = nextZoneId(acc);
        const x = clamp(
          src.x + NEW_ZONE_OFFSET_STEP,
          0,
          REFERENCE_WIDTH_PX - src.w,
        );
        const y = clamp(
          src.y + NEW_ZONE_OFFSET_STEP,
          0,
          REFERENCE_HEIGHT_PX - src.h,
        );
        const clone: Zone = { ...src, id, x, y };
        acc.push(clone);
        cloneIds.push(id);
      }
      setZones(acc);
      selectMany(cloneIds, false);
      setDirty(true);
    },
    [selectMany, setZones],
  );

  // Cmd/Ctrl+C copies the selection here; Cmd/Ctrl+V pastes clones.
  const clipboardRef = useRef<Zone[]>([]);

  const duplicateSelected = useCallback(() => {
    const currentZones = useComposerHistory.getState().zones;
    cloneZonesWithOffset(currentZones.filter((z) => selectedIds.includes(z.id)));
  }, [selectedIds, cloneZonesWithOffset]);

  const copySelected = useCallback(() => {
    const currentZones = useComposerHistory.getState().zones;
    clipboardRef.current = currentZones
      .filter((z) => selectedIds.includes(z.id))
      .map((z) => ({ ...z }));
  }, [selectedIds]);

  const pasteClipboard = useCallback(() => {
    cloneZonesWithOffset(clipboardRef.current);
  }, [cloneZonesWithOffset]);

  // Cmd/Ctrl+C copy, +V paste, +D duplicate the selected zone(s). Skipped
  // while typing in a field so native text copy/paste still works, and
  // gated by edit permission.
  useEffect(() => {
    if (!canEditForNudge) return;
    const onKey = (e: KeyboardEvent): void => {
      if (!(e.metaKey || e.ctrlKey)) return;
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return;
      }
      const key = e.key.toLowerCase();
      if (key === 'c') {
        copySelected();
      } else if (key === 'v') {
        e.preventDefault();
        pasteClipboard();
      } else if (key === 'd') {
        e.preventDefault();
        duplicateSelected();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canEditForNudge, copySelected, pasteClipboard, duplicateSelected]);

  // The properties form operates on the first selected zone.
  const selectedZone = useMemo(
    () => zones.find((z) => z.id === selectedIds[0]) ?? null,
    [zones, selectedIds],
  );

  // Ref attached to the inner canvas div (the 1920×1080 pixel-space element).
  // CanvasMarquee uses it to convert client coordinates to canvas-pixel space.
  const canvasInnerRef = useRef<HTMLDivElement>(null);

  // Called by CanvasMarquee when a drag rectangle is completed.
  // Intersects the marquee rect (canvas-pixel space) with each zone's bounding
  // rect and updates the selection via selectMany (W6 T5 addition to useMultiSelect).
  const handleMarqueeRect = useCallback(
    (rect: { x: number; y: number; w: number; h: number }, shift: boolean) => {
      const currentZones = useComposerHistory.getState().zones;
      const intersecting = currentZones
        .filter((z) => rectsIntersect(rect, { x: z.x, y: z.y, w: z.w, h: z.h }))
        .map((z) => z.id);
      selectMany(intersecting, shift);
    },
    [selectMany],
  );

  // -------------------------------------------------------------------------
  // dnd-kit sensors + drag/resize dispatch
  // -------------------------------------------------------------------------

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent): void => {
      const data = event.active.data.current as
        | { kind: 'zone-body'; zoneId: string }
        | { kind: 'zone-resize'; zoneId: string; handle: ResizeHandle }
        | undefined;
      if (!data) return;

      if (data.kind === 'zone-body') {
        // Translate the drag delta into a new x/y, snap and clamp.
        const currentZones = useComposerHistory.getState().zones;
        const zone = currentZones.find((z) => z.id === data.zoneId);
        if (!zone) return;
        const rawX = zone.x + event.delta.x;
        const rawY = zone.y + event.delta.y;
        const nextZones = currentZones.map((z) => {
          if (z.id !== data.zoneId) return z;
          const w = clamp(Math.max(1, Math.round(z.w)), 1, REFERENCE_WIDTH_PX);
          const h = clamp(Math.max(1, Math.round(z.h)), 1, REFERENCE_HEIGHT_PX);
          const snappedPos = snapToGrid(
            { x: Math.round(rawX), y: Math.round(rawY) },
            gridStep,
          );
          const x = clamp(snappedPos.x, 0, REFERENCE_WIDTH_PX - w);
          const y = clamp(snappedPos.y, 0, REFERENCE_HEIGHT_PX - h);
          return { ...z, x, y, w, h };
        });
        setZones(nextZones);
        setDirty(true);
      } else if (data.kind === 'zone-resize') {
        const { handle } = data;
        const currentZones = useComposerHistory.getState().zones;
        const zone = currentZones.find((z) => z.id === data.zoneId);
        if (!zone) return;

        const next = computeResizedZone(
          zone,
          handle,
          event.delta.x,
          event.delta.y,
          gridStep,
        );
        const nextZones = currentZones.map((z) =>
          z.id === data.zoneId ? { ...z, ...next } : z,
        );
        setZones(nextZones);
        setDirty(true);
      }
    },
    [gridStep, setZones],
  );

  const saveMutation = useMutation<void, ApiError, void>({
    mutationFn: async () => {
      const payload: LayoutPayload = {
        width_px: REFERENCE_WIDTH_PX,
        height_px: REFERENCE_HEIGHT_PX,
        zones: useComposerHistory.getState().zones,
      };
      await saveComposerLayout(composerId, payload);
    },
    onSuccess: () => {
      setSaveMessageKey('composers:layout_editor.save_success');
      setDirty(false);
      void queryClient.invalidateQueries({
        queryKey: ['composers', 'detail', composerId],
      });
      // Keep the saved-geometry cache fresh so a reload (or another tab)
      // reconstructs the canvas from the just-saved zones.
      void queryClient.invalidateQueries({
        queryKey: ['composers', 'layout', composerId],
      });
    },
    onError: () => {
      setSaveMessageKey('composers:layout_editor.save_error');
    },
  });

  // SP3c: register the layout save with the unified ComposerEditor orchestrator
  // (no-ops standalone — no provider). `dirty` is the editor's existing
  // unsaved-changes flag; `save` calls the SAME saveComposerLayout mutation, so
  // the payload is byte-identical — only the trigger moved.
  useRegisterSave({
    key: 'layout',
    order: 2,
    label: t('composers:tab_layout', { defaultValue: 'Mise en page' }),
    dirty,
    save: () => saveMutation.mutateAsync(),
  });

  if (detailQuery.isLoading) {
    return (
      <p
        role="status"
        data-testid="layout-editor-loading"
        className="text-sm text-muted-foreground"
      >
        {t('composers:layout_editor.loading')}
      </p>
    );
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <Alert variant="danger" data-testid="layout-editor-error">
        {t('composers:layout_editor.load_error')}
      </Alert>
    );
  }

  const detail = detailQuery.data;
  const canEdit = hasPermission('composers.update');

  return (
    <div className="flex flex-col gap-4" data-testid="layout-editor-page">
      {/* SP3c: hidden when embedded as the "Mise en page" tab — the shell
          provides the title + the unified save (back/discard/save chrome). */}
      {!embedded && (
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/composers/${composerId}`)}
            data-testid="layout-editor-back"
          >
            {t('common:back')}
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight">
            {pickName(detail.name_i18n, lang)}
          </h1>
          <span
            data-testid={`composer-state-badge-${detail.state}`}
            className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground"
          >
            {t(`composers:state.${detail.state}`)}
          </span>
          <span className="text-sm text-muted-foreground">
            {t('composers:layout_editor.title')}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {dirty && (
            <span
              data-testid="layout-editor-dirty-warning"
              className="text-xs text-amber-700"
            >
              {t('composers:layout_editor.unsaved_changes_warning')}
            </span>
          )}
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setZones(defaultZones());
              useComposerHistory.temporal.getState().clear();
              selectZone(FULL_ZONE_ID, false);
              setDirty(false);
              setSaveMessageKey(null);
            }}
            disabled={!canEdit || !dirty}
            data-testid="layout-editor-discard"
          >
            {t('composers:layout_editor.discard')}
          </Button>
          <Button
            type="button"
            onClick={() => saveMutation.mutate()}
            disabled={!canEdit || saveMutation.isPending}
            data-testid="layout-editor-save"
          >
            {saveMutation.isPending
              ? t('composers:layout_editor.saving')
              : t('composers:layout_editor.save')}
          </Button>
        </div>
      </header>
      )}

      <DndContext
        sensors={sensors}
        onDragMove={(event: DragMoveEvent) => {
          const data = event.active.data.current as
            | { kind?: string; zoneId?: string; handle?: ResizeHandle }
            | undefined;
          // Live resize: recompute geometry every move so the zone visibly
          // grows/shrinks under the pointer instead of jumping on release.
          if (data?.kind === 'zone-resize' && data.zoneId && data.handle) {
            const zone = zones.find((z) => z.id === data.zoneId);
            if (!zone) return;
            const next = computeResizedZone(
              zone,
              data.handle,
              event.delta.x,
              event.delta.y,
              gridStep,
            );
            setLiveResize({ zoneId: zone.id, ...next });
            return;
          }
          if (data?.kind !== 'zone-body' || !data.zoneId) { setGuides([]); return; }
          const moving = zones.find((z) => z.id === data.zoneId);
          if (!moving) return;
          const others = zones.filter((z) => z.id !== data.zoneId);
          const dragged = { id: moving.id, x: moving.x + event.delta.x, y: moving.y + event.delta.y, width: moving.w, height: moving.h };
          setGuides(computeGuides(dragged, others.map((z) => ({ id: z.id, x: z.x, y: z.y, width: z.w, height: z.h }))));
        }}
        onDragEnd={(event: DragEndEvent) => {
          setGuides([]);
          setLiveResize(null);
          handleDragEnd(event);
        }}
        onDragCancel={(_event: DragCancelEvent) => {
          setGuides([]);
          setLiveResize(null);
        }}
      >
        <div className="grid grid-cols-[1fr,320px] gap-4">
          {/* Canvas region — 16:9 reference rectangle, zoom-to-fit */}
          <section
            data-testid="layout-editor-canvas-region"
            aria-label={t('composers:layout_editor.title')}
            className="flex flex-col gap-2 rounded-md border border-border bg-card p-3"
          >
            <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              <span>
                {t('composers:layout_editor.reference_resolution', {
                  width: REFERENCE_WIDTH_PX,
                  height: REFERENCE_HEIGHT_PX,
                })}
              </span>
              <label className="flex items-center gap-2">
                <span>{t('composers:layout_editor.grid_step')}</span>
                <Select
                  value={String(gridStep)}
                  onValueChange={(v) => setGridStep(Number(v) as SnapStep)}
                >
                  <SelectTrigger
                    className="h-8 w-auto"
                    data-testid="composer-grid-step"
                    aria-label={t('composers:layout_editor.grid_step')}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">
                      {t('composers:layout_editor.grid_off')}
                    </SelectItem>
                    <SelectItem value="4">4 px</SelectItem>
                    <SelectItem value="8">8 px</SelectItem>
                    <SelectItem value="16">16 px</SelectItem>
                    <SelectItem value="32">32 px</SelectItem>
                  </SelectContent>
                </Select>
              </label>
              <label className="flex items-center gap-2">
                <span>{t('composers:layout_editor.zoom')}</span>
                <Select
                  value={String(zoom)}
                  onValueChange={(v) => setZoom(Number(v))}
                >
                  <SelectTrigger
                    className="h-8 w-auto"
                    data-testid="layout-editor-zoom-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ZOOM_PRESETS.map((z) => (
                      <SelectItem key={z} value={String(z)}>
                        {Math.round(z * 100)}%
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>
              <button
                type="button"
                data-testid="composer-preview-toggle"
                onClick={() => setPreviewMode((p) => !p)}
                className="rounded border border-input bg-background px-3 py-1 text-sm hover:bg-muted"
              >
                {previewMode
                  ? t('composers:preview_mode_off', { defaultValue: 'Édition' })
                  : t('composers:preview_mode_on', { defaultValue: 'Aperçu' })}
              </button>
              <button
                type="button"
                onClick={() => useComposerHistory.temporal.getState().undo()}
                disabled={pastStates.length === 0}
                aria-label={t('composers:toolbar_undo_aria')}
                data-testid="composer-undo-button"
                className="rounded p-1 hover:bg-muted disabled:opacity-40 disabled:hover:bg-transparent"
              >
                <Icon name="undo-2" size={16} />
              </button>
              <button
                type="button"
                onClick={() => useComposerHistory.temporal.getState().redo()}
                disabled={futureStates.length === 0}
                aria-label={t('composers:toolbar_redo_aria')}
                data-testid="composer-redo-button"
                className="rounded p-1 hover:bg-muted disabled:opacity-40 disabled:hover:bg-transparent"
              >
                <Icon name="redo-2" size={16} />
              </button>
            </div>

            {/* Scaled canvas viewport. The outer wrapper uses Tailwind's
                aspect-video so even when no zones are present the area
                preserves its 16:9 shape. The inner `canvas` is fixed at
                1920×1080 and scaled via `transform: scale(zoom)`; we size
                the wrapper accordingly so the scaled canvas fits inside.
                In previewMode the editable canvas is replaced by a non-
                interactive PreviewPane showing each zone's real content
                (image/video thumbnails via signed URLs, web/rss badges) from
                `previewBindings`; unbound zones fall back to a gradient. */}
            <div
              className="aspect-video w-full overflow-auto rounded-md border border-dashed border-border bg-zinc-50"
              data-testid="layout-editor-viewport"
            >
              {previewMode ? (
                <PreviewPane zones={zones} bindings={previewBindings} />
              ) : (
                <div
                  className="relative origin-top-left"
                  style={{
                    width: REFERENCE_WIDTH_PX * zoom,
                    height: REFERENCE_HEIGHT_PX * zoom,
                  }}
                >
                  <div
                    ref={canvasInnerRef}
                    data-testid="layout-editor-canvas"
                    className="relative bg-white"
                    style={{
                      width: REFERENCE_WIDTH_PX,
                      height: REFERENCE_HEIGHT_PX,
                      transform: `scale(${zoom})`,
                      transformOrigin: 'top left',
                      backgroundImage:
                        'linear-gradient(45deg, #f3f4f6 25%, transparent 25%), linear-gradient(-45deg, #f3f4f6 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f3f4f6 75%), linear-gradient(-45deg, transparent 75%, #f3f4f6 75%)',
                      backgroundSize: '20px 20px',
                      backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
                    }}
                    onMouseDown={(e) => {
                      // Click on empty canvas clears selection.
                      // ZoneDraggable calls e.stopPropagation() (T1), so
                      // clicks ON zones don't reach here.
                      // Shift is excluded: a Shift+drag marquee on empty canvas
                      // must not clear the prior selection before selectMany
                      // unions into it (W6 T5 additive contract).
                      if (e.target === e.currentTarget && !e.shiftKey) {
                        clearSelection();
                      }
                    }}
                  >
                    <CanvasMarquee
                      containerRef={canvasInnerRef}
                      enabled={canEdit && !previewMode}
                      canvasWidthPx={REFERENCE_WIDTH_PX}
                      canvasHeightPx={REFERENCE_HEIGHT_PX}
                      onSelectionRect={handleMarqueeRect}
                    />
                    {zones.map((zone, idx) => {
                      const isSelected = selectedIds.includes(zone.id);
                      const isLocked = lockedIds.includes(zone.id) || !canEdit;
                      // While this zone is being resized, render the transient
                      // live geometry so it tracks the pointer in real time.
                      const live =
                        liveResize?.zoneId === zone.id ? liveResize : zone;
                      return (
                        <ZoneDraggable
                          key={zone.id}
                          zoneId={zone.id}
                          position={{ x: live.x, y: live.y }}
                          size={{ width: live.w, height: live.h }}
                          selected={isSelected}
                          locked={isLocked}
                          zIndex={idx + 1}
                          onSelect={selectZone}
                        >
                          <div
                            className="flex h-full w-full items-center justify-center text-sm font-medium text-slate-700"
                          >
                            <span className="pointer-events-none select-none">
                              {zone.id} · {zone.kind} · {live.w}×{live.h}
                            </span>
                          </div>
                        </ZoneDraggable>
                      );
                    })}
                    <AlignmentGuides guides={guides} />
                  </div>
                </div>
              )}
            </div>

            {saveMessageKey && (
              <p
                role="status"
                data-testid="layout-editor-save-message"
                className={
                  saveMessageKey === 'composers:layout_editor.save_success'
                    ? 'rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-800'
                    : 'rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive'
                }
              >
                {t(saveMessageKey)}
              </p>
            )}
          </section>

          {/* Sidebar — layers panel + add button + properties panel */}
          <aside
            data-testid="layout-editor-sidebar"
            aria-label={t('composers:layout_editor.zone_properties')}
            className="flex flex-col gap-4 rounded-md border border-border bg-card p-3"
          >
            <section className="flex flex-col gap-2">
              <header className="flex items-center justify-between">
                <h2 className="text-sm font-semibold">
                  {t('composers:layout_editor.zones_list')}
                </h2>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={duplicateSelected}
                    disabled={!canEdit || selectedIds.length === 0}
                    data-testid="layout-editor-duplicate-zone"
                  >
                    {t('composers:layout_editor.duplicate_zone', {
                      defaultValue: 'Dupliquer',
                    })}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addZone}
                    disabled={!canEdit}
                    data-testid="layout-editor-add-zone"
                  >
                    {t('composers:layout_editor.add_zone')}
                  </Button>
                </div>
              </header>

              <LayersPanel
                zones={zones}
                selectedIds={selectedIds}
                lockedIds={lockedIds}
                onSelect={selectZone}
                onLockToggle={toggleLock}
                onReorder={setZones}
              />
            </section>

            <section className="flex flex-col gap-2">
              <header>
                <h2 className="text-sm font-semibold">
                  {t('composers:layout_editor.zone_properties')}
                </h2>
              </header>
              {!selectedZone ? (
                <p
                  data-testid="layout-editor-no-selection"
                  className="text-xs text-muted-foreground"
                >
                  {t('composers:layout_editor.no_zone_selected')}
                </p>
              ) : (
                <div
                  data-testid="layout-editor-zone-properties"
                  className="flex flex-col gap-2"
                >
                  <div className="grid grid-cols-2 gap-2">
                    <label className="flex flex-col gap-1 text-xs">
                      <span>{t('composers:layout_editor.x')}</span>
                      <Input
                        type="number"
                        value={selectedZone.x}
                        min={0}
                        max={REFERENCE_WIDTH_PX}
                        step={1}
                        disabled={!canEdit}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          if (!Number.isFinite(v)) return;
                          updateZone(selectedZone.id, { x: v });
                        }}
                        data-testid="layout-editor-input-x"
                        className="h-8"
                      />
                    </label>
                    <label className="flex flex-col gap-1 text-xs">
                      <span>{t('composers:layout_editor.y')}</span>
                      <Input
                        type="number"
                        value={selectedZone.y}
                        min={0}
                        max={REFERENCE_HEIGHT_PX}
                        step={1}
                        disabled={!canEdit}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          if (!Number.isFinite(v)) return;
                          updateZone(selectedZone.id, { y: v });
                        }}
                        data-testid="layout-editor-input-y"
                        className="h-8"
                      />
                    </label>
                    <label className="flex flex-col gap-1 text-xs">
                      <span>{t('composers:layout_editor.width')}</span>
                      <Input
                        type="number"
                        value={selectedZone.w}
                        min={1}
                        max={REFERENCE_WIDTH_PX}
                        step={1}
                        disabled={!canEdit}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          if (!Number.isFinite(v)) return;
                          updateZone(selectedZone.id, { w: v });
                        }}
                        data-testid="layout-editor-input-w"
                        className="h-8"
                      />
                    </label>
                    <label className="flex flex-col gap-1 text-xs">
                      <span>{t('composers:layout_editor.height')}</span>
                      <Input
                        type="number"
                        value={selectedZone.h}
                        min={1}
                        max={REFERENCE_HEIGHT_PX}
                        step={1}
                        disabled={!canEdit}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          if (!Number.isFinite(v)) return;
                          updateZone(selectedZone.id, { h: v });
                        }}
                        data-testid="layout-editor-input-h"
                        className="h-8"
                      />
                    </label>
                  </div>
                  <label className="flex flex-col gap-1 text-xs">
                    <span>{t('composers:layout_editor.kind')}</span>
                    <Select
                      value={selectedZone.kind}
                      disabled={!canEdit}
                      onValueChange={(v) =>
                        updateZone(selectedZone.id, { kind: v as Zone['kind'] })
                      }
                    >
                      <SelectTrigger
                        className="h-8 w-auto"
                        data-testid="layout-editor-select-kind"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ZONE_KINDS.map((k) => (
                          <SelectItem key={k} value={k}>
                            {t(`composers:layout_editor.kind_${k}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </label>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    disabled={!canEdit || zones.length <= 1}
                    onClick={() => removeZone(selectedZone.id)}
                    data-testid="layout-editor-remove-zone"
                  >
                    {t('composers:layout_editor.remove_zone')}
                  </Button>
                </div>
              )}
            </section>

            <Link
              to={`/composers/${composerId}`}
              className="text-xs text-muted-foreground underline-offset-4 hover:underline"
              data-testid="layout-editor-back-link"
            >
              ← {t('common:back')}
            </Link>
          </aside>
        </div>
      </DndContext>

      <PropertiesPanel
        selectedZoneIds={selectedIds}
        composerId={composerId}
        onBindingSaved={() => queryClient.invalidateQueries({ queryKey: ['composers', 'detail', composerId] })}
        programs={detailQuery.data?.programs}
      />
    </div>
  );
}


export default ComposerLayoutEditorPage;
